// Esquemas de validación para archivos JSON del sistema UPS

export const schemas = {
  'news.json': {
    name: 'Noticias',
    description: 'Artículos y noticias del sitio web UPS',
    schema: {
      type: 'array',
      items: {
        type: 'object',
        required: ['id', 'title', 'excerpt', 'content', 'category', 'author', 'date'],
        properties: {
          id: {
            type: 'number',
            minimum: 1,
            description: 'ID único de la noticia'
          },
          title: {
            type: 'string',
            minLength: 10,
            maxLength: 200,
            description: 'Título de la noticia'
          },
          excerpt: {
            type: 'string',
            minLength: 20,
            maxLength: 300,
            description: 'Resumen breve de la noticia'
          },
          content: {
            type: 'string',
            minLength: 50,
            description: 'Contenido completo de la noticia'
          },
          category: {
            type: 'string',
            enum: ['Convocatoria', 'Alianzas', 'Evento', 'Noticia', 'Certificación', 'Académico'],
            description: 'Categoría de la noticia'
          },
          author: {
            type: 'string',
            minLength: 3,
            maxLength: 100,
            description: 'Autor de la noticia'
          },
          date: {
            type: 'string',
            pattern: '^\\d{4}-\\d{2}-\\d{2}$',
            description: 'Fecha de publicación (YYYY-MM-DD)'
          },
          image: {
            type: 'string',
            description: 'URL de la imagen destacada'
          },
          featured: {
            type: 'boolean',
            description: 'Si la noticia está destacada'
          },
          tags: {
            type: 'array',
            items: {
              type: 'string',
              minLength: 2,
              maxLength: 30
            },
            maxItems: 10,
            description: 'Etiquetas de la noticia'
          }
        }
      }
    }
  },

  'resources.json': {
    name: 'Recursos',
    description: 'Recursos descargables del sitio web UPS',
    schema: {
      type: 'array',
      items: {
        type: 'object',
        required: ['id', 'title', 'description', 'category', 'type', 'format', 'downloadUrl', 'date'],
        properties: {
          id: {
            type: 'number',
            minimum: 1,
            description: 'ID único del recurso'
          },
          title: {
            type: 'string',
            minLength: 10,
            maxLength: 200,
            description: 'Título del recurso'
          },
          description: {
            type: 'string',
            minLength: 20,
            maxLength: 500,
            description: 'Descripción del recurso'
          },
          category: {
            type: 'string',
            enum: ['Guías', 'Plantillas', 'Instrumentos', 'Manuales', 'Infografías', 'Videos', 'Documentos'],
            description: 'Categoría del recurso'
          },
          type: {
            type: 'string',
            enum: ['document', 'template', 'tool', 'manual', 'infographic', 'video', 'guide'],
            description: 'Tipo de recurso'
          },
          format: {
            type: 'string',
            enum: ['PDF', 'DOCX', 'XLSX', 'PNG', 'JPG', 'MP4', 'ZIP', 'PPTX'],
            description: 'Formato del archivo'
          },
          size: {
            type: 'string',
            pattern: '^\\d+(\\.\\d+)?\\s*(KB|MB|GB)$',
            description: 'Tamaño del archivo'
          },
          downloadUrl: {
            type: 'string',
            description: 'URL de descarga del recurso'
          },
          date: {
            type: 'string',
            pattern: '^\\d{4}-\\d{2}-\\d{2}$',
            description: 'Fecha de creación (YYYY-MM-DD)'
          },
          downloads: {
            type: 'number',
            minimum: 0,
            description: 'Número de descargas'
          },
          featured: {
            type: 'boolean',
            description: 'Si el recurso está destacado'
          },
          tags: {
            type: 'array',
            items: {
              type: 'string',
              minLength: 2,
              maxLength: 30
            },
            maxItems: 10,
            description: 'Etiquetas del recurso'
          },
          preview: {
            type: 'string',
            description: 'URL de imagen de vista previa'
          }
        }
      }
    }
  },

  'generales.json': {
    name: 'Configuración General',
    description: 'Configuración general del sitio web UPS',
    schema: {
      type: 'object',
      properties: {
        siteName: {
          type: 'string',
          minLength: 3,
          maxLength: 100,
          description: 'Nombre del sitio web'
        },
        version: {
          type: 'string',
          pattern: '^\\d+\\.\\d+\\.\\d+$',
          description: 'Versión del sistema'
        }
      }
    }
  },

  'noticias.json': {
    name: 'Noticias Adicionales',
    description: 'Contenido adicional de noticias',
    schema: {
      type: 'array',
      items: {
        type: 'object',
        properties: {
          id: {
            type: 'number',
            minimum: 1,
            description: 'ID único'
          },
          title: {
            type: 'string',
            minLength: 5,
            description: 'Título'
          },
          content: {
            type: 'string',
            description: 'Contenido'
          }
        }
      }
    }
  }
};

// Validador JSON Schema simplificado
export class JSONValidator {
  static validate(data, schema, path = '') {
    const errors = [];
    
    if (schema.type === 'array') {
      if (!Array.isArray(data)) {
        errors.push({
          path,
          message: 'Debe ser un array',
          severity: 'error'
        });
        return errors;
      }
      
      data.forEach((item, index) => {
        if (schema.items) {
          const itemErrors = this.validate(item, schema.items, `${path}[${index}]`);
          errors.push(...itemErrors);
        }
      });
    }
    
    if (schema.type === 'object') {
      if (typeof data !== 'object' || data === null || Array.isArray(data)) {
        errors.push({
          path,
          message: 'Debe ser un objeto',
          severity: 'error'
        });
        return errors;
      }
      
      // Verificar campos requeridos
      if (schema.required) {
        schema.required.forEach(field => {
          if (!(field in data)) {
            errors.push({
              path: `${path}.${field}`,
              message: `Campo requerido: ${field}`,
              severity: 'error'
            });
          }
        });
      }
      
      // Validar propiedades
      if (schema.properties) {
        Object.keys(data).forEach(key => {
          if (schema.properties[key]) {
            const fieldErrors = this.validate(data[key], schema.properties[key], `${path}.${key}`);
            errors.push(...fieldErrors);
          }
        });
      }
    }
    
    if (schema.type === 'string') {
      if (typeof data !== 'string') {
        errors.push({
          path,
          message: 'Debe ser una cadena de texto',
          severity: 'error'
        });
        return errors;
      }
      
      if (schema.minLength && data.length < schema.minLength) {
        errors.push({
          path,
          message: `Mínimo ${schema.minLength} caracteres (actual: ${data.length})`,
          severity: 'warning'
        });
      }
      
      if (schema.maxLength && data.length > schema.maxLength) {
        errors.push({
          path,
          message: `Máximo ${schema.maxLength} caracteres (actual: ${data.length})`,
          severity: 'error'
        });
      }
      
      if (schema.pattern) {
        const regex = new RegExp(schema.pattern);
        if (!regex.test(data)) {
          errors.push({
            path,
            message: `No cumple con el formato requerido`,
            severity: 'error'
          });
        }
      }
      
      if (schema.enum && !schema.enum.includes(data)) {
        errors.push({
          path,
          message: `Debe ser uno de: ${schema.enum.join(', ')}`,
          severity: 'error'
        });
      }
    }
    
    if (schema.type === 'number') {
      if (typeof data !== 'number') {
        errors.push({
          path,
          message: 'Debe ser un número',
          severity: 'error'
        });
        return errors;
      }
      
      if (schema.minimum !== undefined && data < schema.minimum) {
        errors.push({
          path,
          message: `Debe ser mayor o igual a ${schema.minimum}`,
          severity: 'error'
        });
      }
      
      if (schema.maximum !== undefined && data > schema.maximum) {
        errors.push({
          path,
          message: `Debe ser menor o igual a ${schema.maximum}`,
          severity: 'error'
        });
      }
    }
    
    if (schema.type === 'boolean') {
      if (typeof data !== 'boolean') {
        errors.push({
          path,
          message: 'Debe ser verdadero o falso',
          severity: 'error'
        });
      }
    }
    
    return errors;
  }
  
  static validateFile(filename, data) {
    const schemaInfo = schemas[filename];
    if (!schemaInfo) {
      return {
        valid: true,
        errors: [],
        warnings: []
      };
    }
    
    const errors = this.validate(data, schemaInfo.schema);
    const validationErrors = errors.filter(e => e.severity === 'error');
    const warnings = errors.filter(e => e.severity === 'warning');
    
    return {
      valid: validationErrors.length === 0,
      errors: validationErrors,
      warnings: warnings,
      schema: schemaInfo
    };
  }
}

// Generadores de ejemplos
export const exampleGenerators = {
  'news.json': () => ({
    id: Date.now(),
    title: "Nuevo artículo de ejemplo",
    excerpt: "Este es un resumen breve del artículo que explica de qué trata el contenido.",
    content: "Este es el contenido completo del artículo. Aquí puedes incluir toda la información detallada sobre el tema que quieres compartir con los lectores.",
    category: "Noticia",
    author: "Equipo Editorial UPS",
    date: new Date().toISOString().split('T')[0],
    image: "https://images.unsplash.com/photo-1586271391555-0d7e3e7d50d2?ixlib=rb-4.0.3&auto=format&fit=crop&w=800&q=80",
    featured: false,
    tags: ["ejemplo", "nuevo"]
  }),

  'resources.json': () => ({
    id: Date.now(),
    title: "Nuevo recurso de ejemplo",
    description: "Descripción detallada del recurso que explica qué contiene y para qué sirve.",
    category: "Documentos",
    type: "document",
    format: "PDF",
    size: "1.2 MB",
    downloadUrl: "#",
    date: new Date().toISOString().split('T')[0],
    downloads: 0,
    featured: false,
    tags: ["ejemplo", "nuevo"],
    preview: "https://images.unsplash.com/photo-1453748866136-b1dd97319208?ixlib=rb-4.0.3&auto=format&fit=crop&w=400&q=80"
  })
};
